import { Box, FormControlLabel, Grid, Switch, TextField, Typography } from '@mui/material';
import type React from 'react';
import { type Control, Controller, type FieldErrors } from 'react-hook-form';
import type { ClientDetailsFormData } from '../../schemas/clientDetailsSchema';

interface Props {
  control: Control<ClientDetailsFormData>;
  errors: FieldErrors<ClientDetailsFormData>;
  isStripeManaged: boolean;
}

export const SettingsTab: React.FC<Props> = ({ control, errors, isStripeManaged }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Controller
        name="clientName"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="Client Name"
            fullWidth
            disabled
            inputProps={{ maxLength: 100 }}
            error={!!errors.clientName}
            helperText={errors.clientName?.message || `${field.value?.length || 0}/100`}
          />
        )}
      />
      <Controller
        name="primaryContactEmail"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="Primary Contact Email"
            fullWidth
            inputProps={{ maxLength: 255 }}
            error={!!errors.primaryContactEmail}
            helperText={errors.primaryContactEmail?.message || `${field.value?.length || 0}/255`}
          />
        )}
      />
      <Controller
        name="logo"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="Logo URL"
            fullWidth
            disabled
            error={!!errors.logo}
            helperText={errors.logo?.message}
          />
        )}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="h6" gutterBottom color="primary">
          Feature Access
        </Typography>
        <Controller
          name="isDemo"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Switch checked={field.value} onChange={field.onChange} color="primary" />}
              label={
                <Box>
                  <Typography variant="body1">Demo Environment</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Mark this client as an internal demo account for tracking and isolation.
                  </Typography>
                </Box>
              }
            />
          )}
        />
        <Controller
          name="hasMatchmakingAccess"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Switch checked={field.value} onChange={field.onChange} color="primary" />}
              label={
                <Box>
                  <Typography variant="body1">Enable Subcontractor Matchmaking</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Allow this client to use the Subcontractor Matchmaking service.
                  </Typography>
                </Box>
              }
            />
          )}
        />
        <Controller
          name="enableConneqtAccess"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Switch checked={field.value} onChange={field.onChange} color="primary" />}
              label={
                <Box>
                  <Typography variant="body1">Enable CONNEQT App Access</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Allow users from this organization to log into the CONNEQT application.
                  </Typography>
                </Box>
              }
            />
          )}
        />
        <Controller
          name="enableBiddingModule"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Switch checked={field.value} onChange={field.onChange} color="primary" />}
              label={
                <Box>
                  <Typography variant="body1">Enable Bidding Module (Sell-Side)</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Allow this client to use the core Tender/Proposal Writing workspace.
                  </Typography>
                </Box>
              }
            />
          )}
        />
        <Controller
          name="enableProcurementModule"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Switch checked={field.value} onChange={field.onChange} color="primary" />}
              label={
                <Box>
                  <Typography variant="body1">Enable Procurement Module (Buy-Side)</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Allow this client to use the Procurement and Supplier Evaluation workspace.
                  </Typography>
                </Box>
              }
            />
          )}
        />
        <Controller
          name="isIsolatedInstance"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Switch checked={field.value} onChange={field.onChange} color="primary" />}
              label={
                <Box>
                  <Typography variant="body1">Isolated Instance</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Run this client in an isolated instance (requires separate infrastructure
                    setup).
                  </Typography>
                </Box>
              }
            />
          )}
        />
      </Box>
      <Box>
        <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
          Organization Seat Limits
        </Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name="limits.numUsers"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  label="Max Users"
                  type="number"
                  fullWidth
                  helperText={errors.limits?.numUsers?.message || 'Hard cap on active users.'}
                  error={!!errors.limits?.numUsers}
                  disabled={isStripeManaged}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name="limits.numTeams"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  label="Max Teams"
                  type="number"
                  fullWidth
                  helperText={errors.limits?.numTeams?.message || 'Hard cap on teams.'}
                  error={!!errors.limits?.numTeams}
                  disabled={isStripeManaged}
                />
              )}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
